/**
 * Base API client with authentication and error handling
 * 
 * Provides a configured fetch wrapper with:
 * - Cognito JWT token injection
 * - Automatic token refresh on 401
 * - Consistent error transformation
 * - Correlation ID logging
 */
import type { ApiResponse, ApiError } from './types';

/** API configuration */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/** Token storage keys */
const ID_TOKEN_KEY = 'idToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

/**
 * Get the current ID token from storage
 * We use ID token instead of access token because it contains user attributes (name, email)
 */
function getIdToken(): string | null {
  return localStorage.getItem(ID_TOKEN_KEY);
}

/**
 * Get the current refresh token from storage
 */
function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Store tokens after authentication
 */
export function setTokens(idToken: string, refreshToken: string): void {
  localStorage.setItem(ID_TOKEN_KEY, idToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/**
 * Clear tokens on logout
 */
export function clearTokens(): void {
  localStorage.removeItem(ID_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Generate a correlation ID for request tracing
 *
 * The random suffix comes from the WebCrypto API so correlation IDs are not
 * predictable from a sampled Math.random() sequence.
 */
function generateCorrelationId(): string {
  const bytes = new Uint8Array(5);
  crypto.getRandomValues(bytes);
  const random = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('').substring(0, 9);
  return `${Date.now()}-${random}`;
}

/**
 * Attempt to refresh the access token using the refresh token
 * @returns New access token or null if refresh failed
 */
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      clearTokens();
      return null;
    }

    const data = await response.json();
    if (data.data?.idToken) {
      localStorage.setItem(ID_TOKEN_KEY, data.data.idToken);
      return data.data.idToken;
    }
    return null;
  } catch {
    clearTokens();
    return null;
  }
}


/**
 * Transform fetch errors to consistent ApiError format
 */
function createApiError(
  code: string,
  message: string,
  details?: Record<string, unknown>
): ApiError {
  return { code, message, details };
}

/**
 * Create an error response with metadata
 */
function createErrorResponse<T>(
  error: ApiError,
  correlationId: string
): ApiResponse<T> {
  return {
    data: null,
    error,
    meta: {
      correlationId,
      timestamp: new Date().toISOString(),
    },
  };
}

/**
 * Log request/response for debugging
 */
function logRequest(
  method: string,
  url: string,
  correlationId: string,
  duration: number,
  status?: number
): void {
  if (import.meta.env.MODE === 'development') {
    console.log(
      `[API] ${method} ${url} | correlationId=${correlationId} | status=${status ?? 'error'} | duration=${duration}ms`
    );
  }
}

/** Request options */
interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  skipAuth?: boolean;
  /** Additional headers to include in the request */
  headers?: Record<string, string>;
}

/**
 * Make an authenticated API request
 */
async function request<T>(
  endpoint: string,
  options: RequestOptions
): Promise<ApiResponse<T>> {
  const correlationId = generateCorrelationId();
  const startTime = Date.now();
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Correlation-ID': correlationId,
    ...options.headers,
  };

  // Add auth token if available and not skipped
  if (!options.skipAuth) {
    const token = getIdToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const fetchOptions: RequestInit = {
    method: options.method,
    headers,
  };

  if (options.body !== undefined) {
    fetchOptions.body = JSON.stringify(options.body);
  }

  try {
    let response = await fetch(url, fetchOptions);

    // Handle 401 with token refresh
    if (response.status === 401 && !options.skipAuth) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, { ...fetchOptions, headers });
      }
    }

    const duration = Date.now() - startTime;
    logRequest(options.method, endpoint, correlationId, duration, response.status);

    // Parse response body
    const data = await response.json() as ApiResponse<T>;

    // If the response already has our expected structure, return it
    if ('data' in data && 'meta' in data) {
      return data;
    }

    // Otherwise, wrap the response
    return {
      data: data as T,
      error: null,
      meta: {
        correlationId,
        timestamp: new Date().toISOString(),
      },
    };
  } catch (error) {
    const duration = Date.now() - startTime;
    logRequest(options.method, endpoint, correlationId, duration);

    const apiError = createApiError(
      'NETWORK_ERROR',
      error instanceof Error ? error.message : 'Network request failed'
    );

    return createErrorResponse<T>(apiError, correlationId);
  }
}


/**
 * API client with typed methods for each HTTP verb
 */
export const apiClient = {
  /**
   * Make a GET request
   */
  get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return request<T>(endpoint, { method: 'GET' });
  },

  /**
   * Make a POST request
   */
  post<T>(endpoint: string, body?: unknown, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return request<T>(endpoint, { method: 'POST', body, headers });
  },

  /**
   * Make a PUT request
   */
  put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>(endpoint, { method: 'PUT', body });
  },

  /**
   * Make a DELETE request
   */
  delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return request<T>(endpoint, { method: 'DELETE' });
  },

  /**
   * Make a PATCH request
   */
  patch<T>(endpoint: string, body?: unknown, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return request<T>(endpoint, { method: 'PATCH', body, headers });
  },
};
